import React from 'react';
import classNames from 'classnames/bind';
import { DropOptions, NodeModel, Tree } from '@minoru/react-dnd-treeview';

import { OffsetPaginated } from 'types/pagination';
import { Tag } from 'types/tag';

import { useHasAccess } from 'hooks/useHasAccess';
import { useMap } from 'hooks/useMap';
import { useTags } from 'tags/hooks/useTags';
import { useTagsAncestors } from 'tags/hooks/useTagsAncestors';
import { useTagsTreeStructure } from 'tags/hooks/useTagsTreeStructure';
import { useUpdateTag } from 'tags/hooks/useUpdateTag';

import { Text } from 'components/Text/Text';

import { TagsTreeItem } from './Item';
import styles from './TagsTree.module.scss';

const c = classNames.bind(styles);

export type TagsTreeProps = {
  selection?: Tag['id'][];
  searchTerm?: string;
  onAdd?: (tag: Tag) => void;
  onRemove?: (tagId: Tag['id']) => void;
};

/**
 * Component to navigate between tags and edit their hierarchy.
 * User-facing term is `label`.
 */
export function TagsTree({
  searchTerm,
  selection,
  onAdd,
  onRemove,
}: TagsTreeProps) {
  const [hasAccess] = useHasAccess();

  const { mutate: updateTag } = useUpdateTag();

  const { data: rootData } = useTags({ params: { only_roots: true } });

  const {
    data: searchResult,
    isSuccess,
    error,
  } = useTags({
    params: {
      tag_names: searchTerm ? [searchTerm] : undefined,
      limit: 100,
    },
    keepPreviousData: true,
    enabled: Boolean(searchTerm?.trim()),
  });

  const {
    data: ancestors,
    isLoading: isLoadingAncestors,
    error: ancestorsError,
  } = useTagsAncestors(searchResult, {
    enabled: isSuccess,
    staleTime: Infinity,
  });

  const [children, { set: setChildren }] = useMap<Tag['id'], Tag[]>();

  const allTags = React.useMemo(() => {
    const combinedTags = rootData
      ? [...rootData.data, ...[...children.values()].flat()]
      : [];

    return [...new Map(combinedTags.map((tag) => [tag.id, tag])).values()];
  }, [children, rootData]);

  const tree = useTagsTreeStructure(allTags, true);

  const canSelect = Boolean(onAdd);

  let searchTags: Tag[] | undefined = undefined;

  if (
    searchResult &&
    !ancestors.some((x) => !x) &&
    !isLoadingAncestors &&
    searchTerm?.trim()
  ) {
    const combined = [
      ...searchResult.data,
      ...(ancestors as OffsetPaginated<Tag>[]).flatMap(({ data }) => data),
    ];
    searchTags = [...new Map(combined.map((tag) => [tag.id, tag])).values()];
  }

  const searchTree = useTagsTreeStructure(searchTags);

  // Disable drag & drop for selectable tree nodes and all + untagged
  function canDrag(node: NodeModel<Tag> | undefined): boolean {
    if (!hasAccess('deploy')) {
      return false;
    }

    if (canSelect || node?.id === 'all' || node?.id === 'none') {
      return false;
    }

    return node?.droppable ?? false;
  }

  // Disable drag & drop for selectable tree nodes and all + untagged
  function canDrop(_tree: NodeModel<Tag>[], options: DropOptions<Tag>) {
    if (!hasAccess('deploy')) {
      return false;
    }

    const { dropTargetId, dragSourceId } = options;

    return (
      !canSelect &&
      dropTargetId !== 'all' &&
      dropTargetId !== 'none' &&
      dragSourceId !== dropTargetId
    );
  }

  function handleDrop(
    _tree: NodeModel<Tag>[],
    { dragSource, dropTarget }: DropOptions<Tag>
  ) {
    if (!dragSource?.data) {
      return;
    }

    const tag = structuredClone(dragSource.data);

    if (!dropTarget || !dropTarget.data) {
      tag.parent = null;
    } else {
      tag.parent = dropTarget.data.id;
    }

    updateTag(tag);
  }

  function handleItemSelectionChange(tag: Tag, checked: boolean) {
    if (checked) {
      onAdd?.(tag);
    } else {
      onRemove?.(tag.id);
    }
  }

  function hasSelectedChildren(tag?: Tag): boolean {
    if (!tag?.children) {
      return false;
    }

    return tag.children.some((childId) => {
      const childTag = tree?.find((node) => node.id === childId)?.data;
      return selection?.includes(childId) || hasSelectedChildren(childTag);
    });
  }

  return (
    <div className={c('wrap')}>
      {(error || ancestorsError) && (
        <Text intent="danger" size="xsmall">
          {error?.message ?? ancestorsError?.error?.message}
        </Text>
      )}

      {(tree || searchTree) && (
        <Tree
          tree={searchTree ?? tree!}
          rootId={0}
          classes={{
            root: c('root'),
            dropTarget: c('drop-target'),
          }}
          render={(item, props) => (
            <TagsTreeItem
              {...props}
              node={item}
              tag={item.data}
              isSelected={selection?.includes(String(item.id))}
              isIndeterminate={hasSelectedChildren(item.data)}
              onLoad={setChildren}
              onSelectionChange={
                canSelect ? handleItemSelectionChange : undefined
              }
            />
          )}
          canDrag={canDrag}
          canDrop={canDrop}
          dragPreviewRender={(monitorProps) => (
            <span>{monitorProps.item.text}</span>
          )}
          // Sort manually to keep `all` and `none` in place
          sort={false}
          onDrop={handleDrop}
          initialOpen={Boolean(searchTree)}
        />
      )}

      {searchTree && searchTree.length === 0 && (
        <Text align="center">No matching labels found.</Text>
      )}
    </div>
  );
}
